// deps
import { useCallback, useContext, useState } from "react";

// internal-deps
import {
    BOOLEAN_AS_STRING,
    DineoutCartContext,
    DineoutCartType,
    IResponseCartItem,
} from "@swiggy-private/checkout-core";

// interfaces
import { ILocalCartItem } from "@dineout-cart-client/interfaces/LocalCart";
import { isEventReservationCart, isStealStoreCart } from "@dineout-cart-client/helpers/cartHelper";
import { SERVICE_LINE } from "@dineout-cart-client/constants";
import { getGroupCartRendererItems } from "@dineout-cart-client/helpers/StealDealCart";
import { UpdateCartContext } from "@dineout-cart-client/contexts/UpdateCartContext";

/** Only serializable fields for API/local storage. Avoids passing UI-only fields (e.g. React elements) that cause JSON.stringify circular ref. */
const toSerializableCartItem = (item: ILocalCartItem): ILocalCartItem => ({
    itemId: item.itemId,
    quantity: item.quantity,
    itemPrice: item.itemPrice,
    itemVariantId: item.itemVariantId,
    meta: item.meta ?? {},
    serviceLine: item.serviceLine ?? SERVICE_LINE,
    dealCategory: item.dealCategory,
    selectedSlot: item.selectedSlot,
    min: item.min,
    max: item.max,
    outOfStock: item.outOfStock,
});

interface IProps {
    onClickTicketItem?: (item: ILocalCartItem) => void;
    onClickDeleteTicketItem?: (item: ILocalCartItem) => void;
    eventsProceedButtonEvent?: VoidFunction;
    cartType: DineoutCartType;
}

interface IReturnType {
    onItemQuantityUpdate: (item: ILocalCartItem) => void;
    onDeleteItem: (item: ILocalCartItem) => void;
    onRemoveUnavailableItems: VoidFunction;
    updatingItem: ILocalCartItem | null;
    onClearCart: VoidFunction;
    onRemoveExcessItems: VoidFunction;
}

const useDineoutCartItemActions = (props: IProps): IReturnType => {
    const { onClickTicketItem, onClickDeleteTicketItem, eventsProceedButtonEvent, cartType } = props;
    const { cartState: cartPayload } = useContext(DineoutCartContext);
    const { localCart, reviewedCart } = cartPayload;
    const cartState = reviewedCart?.data;

    const { updateCartLoading, doUpdateCart } = useContext(UpdateCartContext);

    const [updatingItem, setUpdatingItem] = useState<ILocalCartItem | null>(null);

    const onItemQuantityUpdate = useCallback(
        async (item: ILocalCartItem) => {
            onClickTicketItem?.(item);
            setUpdatingItem(item);
            // get other items in list
            const updatedCartItemsList = localCart?.items?.filter((cartItem: ILocalCartItem) => {
                if (isEventReservationCart(cartType))
                    return String(cartItem.meta?.variantId) !== String(item.meta?.variantId);
                return String(cartItem.itemId) !== String(item.itemId);
            });

            // if updated item quantity is non zero / negative push it in updated items list (serializable only - no React elements)
            if (item.quantity && item.quantity > 0) {
                updatedCartItemsList?.push(toSerializableCartItem({ ...item }));
            }

            await doUpdateCart({ ...localCart, items: updatedCartItemsList ?? [] });
            setUpdatingItem(null);
        },
        [cartType, localCart, onClickTicketItem, doUpdateCart],
    );

    const onDeleteItem = useCallback(
        async (item: ILocalCartItem) => {
            onClickDeleteTicketItem?.(item);
            setUpdatingItem(item);
            // remove the item from the list
            const updatedCartItemsList = localCart?.items?.filter((cartItem: ILocalCartItem) => {
                if (isEventReservationCart(cartType))
                    return String(cartItem.meta?.variantId) !== String(item.meta?.variantId);
                return String(cartItem.itemId) !== String(item.itemId);
            });

            // perform the update cart operation
            await doUpdateCart({ ...localCart, items: updatedCartItemsList ?? [] });
            setUpdatingItem(null);
        },
        [cartType, localCart, onClickDeleteTicketItem, doUpdateCart],
    );

    const onRemoveUnavailableItems = useCallback(async () => {
        if (updateCartLoading) return;
        eventsProceedButtonEvent?.();
        // remove all items with outOfStock true
        const updatedCartItemsList = localCart?.items?.filter((cartItem: ILocalCartItem) => {
            const correspondingRegularItem = cartState?.items?.find(
                (reviewedCartItem: IResponseCartItem) =>
                    String(cartItem?.meta?.variantId) === String(reviewedCartItem.itemVariantId),
            );

            let correspondingStealDealItem: IResponseCartItem | undefined;

            if (isStealStoreCart(cartType)) {
                const { items: stealDealItems } = getGroupCartRendererItems(cartState);
                correspondingStealDealItem = stealDealItems?.find(
                    (stealDealItem: IResponseCartItem) => String(cartItem?.itemId) === String(stealDealItem.itemId),
                );
            }

            const correspondingItem = isStealStoreCart(cartType)
                ? correspondingStealDealItem
                : correspondingRegularItem;

            return (
                String(correspondingItem?.minimumQtyBreach) !== BOOLEAN_AS_STRING.TRUE &&
                String(correspondingItem?.outOfStock) !== BOOLEAN_AS_STRING.TRUE
            );
        });
        // perform the update cart operation
        await doUpdateCart({ ...localCart, items: updatedCartItemsList });
    }, [eventsProceedButtonEvent, localCart, doUpdateCart, cartState?.items, cartType, updateCartLoading]);

    const onRemoveExcessItems = useCallback(async () => {
        if (updateCartLoading) return;
        eventsProceedButtonEvent?.();
        await doUpdateCart({
            ...localCart,
            cartMetaData: localCart?.cartMetaData
                ? { ...localCart.cartMetaData, adjustToAvailableQuantity: true }
                : undefined,
        });
    }, [eventsProceedButtonEvent, localCart, doUpdateCart, updateCartLoading]);

    const onClearCart = useCallback(async () => {
        // perform the update cart operation with empty items list
        await doUpdateCart({ ...localCart, items: [] });
    }, [localCart, doUpdateCart]);

    return {
        onItemQuantityUpdate,
        onDeleteItem,
        onRemoveUnavailableItems,
        updatingItem,
        onClearCart,
        onRemoveExcessItems,
    };
};

export default useDineoutCartItemActions;
